import Axios from "axios";
import CONFIG from "../../model/TSConfig";
import { type POSADeliveryWithBill } from "../12_POSAManager/api";
import { type POSAOrderItem } from "../../redux/saas/reducer";
import type BusinessUser from "@tootsweet/model/entities/BusinessUser";
import { type UploadImageDatasetI } from "../../model/UploadImageDatasetI";
import ReturnCodes from "../../utils/error_handling/ReturnCodes";
import { generatePDFFromPNG } from "../../utils/helpers/generatePDFFromPNG";
import { jsPDF } from "jspdf";

export async function updateCustomer(
  fieldsToUpdate: Partial<BusinessUser>,
  id: string | undefined | null,
  token: string | undefined
) {
  if (!id || !token) {
    return;
  }
  const updateCustomerResponse = await Axios({
    method: "PUT",
    url: `${CONFIG.saasBackendUrl}/customers/${id}`,
    headers: { Authorization: `Bearer ${token}` },
    data: fieldsToUpdate
  });

  return updateCustomerResponse.data;
}

export async function assignFreePlan(
  trackingID: string,
  planId: string,
  planName: string,
  token: string | undefined
) {
  if (!token) {
    return;
  }
  const response = await Axios({
    method: "POST",
    url: `${CONFIG.saasBackendUrl}/subscription/free`,
    headers: { Authorization: `Bearer ${token}` },
    data: {
      trackingID,
      planId,
      planName
    }
  });

  return response.data;
}

export const jetpackUploadLogoToBucket = async (
  userId: string | undefined,
  dataset: UploadImageDatasetI,
  token: string | undefined
) => {
  if (!token || !userId) return;
  return await Axios.post(
    `${CONFIG.saasBackendUrl}/jetpack-config/${userId}/logo_jetpack`,
    { dataset },
    { headers: { Authorization: `Bearer ${token}` } }
  );
};

interface CustomPOSAPDFData {
  posaTypeId: string;
  pdf: jsPDF;
}

export async function createPOSADeliveryAndBills(
  businessUserId: string,
  token: string,
  saasFlyersItems: POSAOrderItem[]
): Promise<POSADeliveryWithBill[]> {
  const typesAndQuantities: any = {};

  saasFlyersItems.forEach((item) => {
    if (!item.posaTypeId) {
      return;
    }
    typesAndQuantities[item.posaTypeId] = item?.qty;
  });

  try {
    const customPOSAQuery = saasFlyersItems
      .filter((item) => item.posaTypeId)
      .map((item) => item.posaTypeId);

    const customPOSAs = await Axios.get(
      `${CONFIG.saasBackendUrl}/posa/custom-posa/batch`,
      {
        params: {
          businessUserId,
          posaTypeIds: customPOSAQuery.join(",")
        },
        headers: { Authorization: `Bearer ${token}` }
      }
    );

    const pdfData: CustomPOSAPDFData[] = [];

    for (const customPOSA of customPOSAs.data || []) {
      if (customPOSA.url) {
        try {
          const pngUrl = `${CONFIG.s3BucketURL}/${customPOSA.url}`;
          const pdf = await generatePDFFromPNG(pngUrl, customPOSA.format);
          pdfData.push({
            posaTypeId: customPOSA.posaTypeId,
            pdf
          });
        } catch (error) {
          console.error(
            `Failed to generate PDF for ${customPOSA.posaTypeId}:`,
            error
          );
          throw new Error(
            JSON.stringify({
              code: ReturnCodes.PDFGenerationFailed,
              posaTypeId: customPOSA.posaTypeId
            })
          );
        }
      }
    }

    const pdfFilesBase64: Record<string, string> = {};

    for (const pdf of pdfData) {
      pdfFilesBase64[pdf.posaTypeId] = pdf.pdf
        .output("datauristring")
        .split(",")[1];
    }

    const res = await Axios.post(
      `${CONFIG.saasBackendUrl}/posa-delivery/create-with-validation`,
      {
        businessUserId,
        typesAndQuantities: typesAndQuantities,
        pdfFiles: pdfFilesBase64
      },
      {
        headers: {
          Authorization: `Bearer ${token}`
        }
      }
    );

    if (res.data.code && res.data.code !== ReturnCodes.Ok) {
      throw new Error(JSON.stringify(res.data));
    }

    return res.data.deliveries;
  } catch (error: any) {
    if (error.response?.data?.code) {
      throw error.response.data;
    }
    throw error;
  }
}
